/**
 * ArsenalTeaserCard - Full-width Arsenal intelligence card with FOMO badges
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePVRedirect } from '@/hooks/usePVRedirect';
import { Shield, ArrowRight, Lock, ExternalLink, Flame, TrendingUp } from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logRequest } from '@/hooks/useRequestLogger';
import { useRole } from '@/contexts/RoleContext';
import { Skeleton } from '@/components/ui/skeleton';
import { OptimizedImage } from '@/components/OptimizedImage';

interface TeaserVideo {
  id: string;
  thumbnail_url: string | null;
  published_at: string | null;
  views_count: number | null;
}

async function fetchArsenalTeaserWithMeta(): Promise<TeaserVideo[]> {
  const { data, error } = await supabase
    .from('arsenal_videos')
    .select('id, thumbnail_url, published_at, views_count')
    .eq('intention', 'tiktok_shop')
    .eq('publication_type', 'normal')
    .not('thumbnail_url', 'is', null)
    .order('published_at', { ascending: false })
    .limit(30);

  if (error) return [];
  logRequest('arsenal_videos');
  return (data ?? []) as TeaserVideo[];
}

function isToday(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.toDateString() === now.toDateString();
}

function daysSince(dateStr: string | null): number {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  return (Date.now() - d.getTime()) / (24 * 60 * 60 * 1000);
}

type BadgeKind = 'today' | 'new' | 'trending' | 'revived' | null;

// Mulberry32 PRNG seeded by date string → stable order per day
function seededShuffle<T>(arr: T[], seedStr: string): T[] {
  let h = 1779033703 ^ seedStr.length;
  for (let i = 0; i < seedStr.length; i++) {
    h = Math.imul(h ^ seedStr.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  const rand = () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function todaySeed(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function computeDisplayList(videos: TeaserVideo[]): { video: TeaserVideo; badge: BadgeKind }[] {
  if (videos.length === 0) return [];

  const hasFreshPost = videos.some((v) => daysSince(v.published_at) < 3);

  if (hasFreshPost) {
    // FRESH mode: chronological. First 3 always carry "NOVO" (or "HOJE" if same day).
    return videos.slice(0, 8).map((video, idx) => {
      let badge: BadgeKind = null;
      if (idx < 3) badge = isToday(video.published_at) ? 'today' : 'new';
      else if (isToday(video.published_at)) badge = 'today';
      return { video, badge };
    });
  }

  // REVIVE mode: deterministic daily shuffle. Surface "EM ALTA" + "RESGATADO".
  const shuffled = seededShuffle(videos, todaySeed()).slice(0, 8);
  return shuffled.map((video, idx) => {
    let badge: BadgeKind = null;
    if (idx < 2) badge = 'trending';
    else if (idx === 3) badge = 'revived';
    return { video, badge };
  });
}

function ArsenalTeaserCardInner() {
  const navigate = useNavigate();
  const { effectiveRole } = useRole();
  const { redirectToPV } = usePVRedirect();
  const isVisitor = effectiveRole === 'visitor';

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['arsenal', 'teaser-with-meta'],
    queryFn: fetchArsenalTeaserWithMeta,
    staleTime: 5 * 60 * 1000,
  });

  const displayList = React.useMemo(() => computeDisplayList(videos), [videos]);

  const handleClick = () => {
    if (isVisitor) {
      redirectToPV('arsenal-teaser');
    } else {
      navigate('/arsenal');
    }
  };

  return (
    <div className="forja-card rounded-xl p-3 md:p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20">
            <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
          </div>
          <div>
            <h2 className="font-display text-sm md:text-base font-bold text-foreground">
              Arsenal de Inteligência da Forja
            </h2>
            <p className="text-[9px] md:text-[10px] text-muted-foreground">
              Vídeos virais e estratégias que estão vendendo agora.
            </p>
          </div>
        </div>
      </div>

      {/* Thumbnails - horizontal scroll */}
      {isLoading ? (
        <div className="flex gap-2.5 overflow-hidden mb-3">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="w-[80px] md:w-[100px] aspect-[9/16] rounded-lg flex-shrink-0" />
          ))}
        </div>
      ) : displayList.length > 0 ? (
        <div className="relative mb-3">
          <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
            {displayList.map(({ video, badge }) => (
              <div
                key={video.id}
                className="relative w-[80px] md:w-[100px] aspect-[9/16] rounded-lg overflow-hidden flex-shrink-0 border border-border/30 cursor-pointer group/thumb"
                onClick={handleClick}
              >
                <OptimizedImage
                  src={video.thumbnail_url!}
                  alt=""
                  className="absolute inset-0 w-full h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
                
                {/* FOMO badges */}
                {badge === 'today' && (
                  <div className="absolute top-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/90 backdrop-blur-sm">
                    <Flame className="w-2.5 h-2.5 text-white" />
                    <span className="text-[7px] font-bold text-white">HOJE</span>
                  </div>
                )}
                {badge === 'new' && (
                  <div className="absolute top-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/80 backdrop-blur-sm">
                    <TrendingUp className="w-2.5 h-2.5 text-white" />
                    <span className="text-[7px] font-bold text-white">NOVO</span>
                  </div>
                )}
                {badge === 'trending' && (
                  <div className="absolute top-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/80 backdrop-blur-sm">
                    <TrendingUp className="w-2.5 h-2.5 text-white" />
                    <span className="text-[7px] font-bold text-white">EM ALTA</span>
                  </div>
                )}
                {badge === 'revived' && (
                  <div className="absolute top-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/80 backdrop-blur-sm">
                    <Sparkles className="w-2.5 h-2.5 text-white" />
                    <span className="text-[7px] font-bold text-white">RESGATADO</span>
                  </div>
                )}

                {isVisitor && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Lock className="w-3.5 h-3.5 text-white/70" />
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Fade edges */}
          <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card/80 to-transparent pointer-events-none" />
        </div>
      ) : (
        <div className="flex items-center justify-center h-[80px] rounded-lg bg-secondary/30 border border-border/30 mb-3">
          <p className="text-[10px] text-muted-foreground">Conteúdo sendo preparado...</p>
        </div>
      )}

      {/* CTA */}
      <div className="mt-auto">
      <Button
        size="sm"
        variant={isVisitor ? 'outline' : 'ghost'}
        className="w-full gap-1.5 text-xs"
        onClick={handleClick}
      >
        {isVisitor ? (
          <>Quero ser membro <ExternalLink className="w-3.5 h-3.5" /></>
        ) : (
          <>Explorar Arsenal <ArrowRight className="w-3.5 h-3.5" /></>
        )}
      </Button>
      </div>
    </div>
  );
}

const ArsenalTeaserCard = React.memo(ArsenalTeaserCardInner);
export default ArsenalTeaserCard;
